/**
 * Main App Component
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { AssessmentList } from './pages/AssessmentList';
import { AssessmentDetail } from './pages/AssessmentDetail';
import { AssessmentPlay } from './pages/AssessmentPlay';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminAssessmentEdit } from './pages/AdminAssessmentEdit';
import { AdminQuestionBankList } from './pages/AdminQuestionBankList';
import { AdminQuestionBankEdit } from './pages/AdminQuestionBankEdit';
import { ResultsList } from './pages/ResultsList';
import { ResultDetail } from './pages/ResultDetail';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading, isAdmin } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isAdmin, loading } = useAuth();

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-content">
          <div className="app-header-brand">
            <img
              src="https://cdn.prod.website-files.com/68719e50f0b3a42d461aa8b0/6872beb88b459659a56f1498_pathful-logo.png"
              alt="Pathful Logo"
              className="app-logo"
            />
            <h1>Assessment Engine</h1>
          </div>
          <nav className="app-nav">
            {!loading && isAdmin && (
              <>
                <Link to="/assessments">Assessments</Link>
                <Link to="/admin">Build Assessments</Link>
                <Link to="/admin/question-banks">Question Banks</Link>
                <Link to="/results">Results</Link>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<div className="landing"><h2>Welcome to the Pathful Assessment Engine</h2><p>You will be directed to your assessment when it is time to begin.</p></div>} />
          <Route path="/assessments" element={<AdminRoute><AssessmentList /></AdminRoute>} />
          <Route path="/assessments/:id" element={<AssessmentDetail />} />
          <Route path="/assessments/:id/play/:attemptId" element={<AssessmentPlay />} />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/assessments/new" element={<AdminRoute><AdminAssessmentEdit /></AdminRoute>} />
          <Route path="/admin/assessments/:id" element={<AdminRoute><AdminAssessmentEdit /></AdminRoute>} />
          <Route path="/admin/question-banks" element={<AdminRoute><AdminQuestionBankList /></AdminRoute>} />
          <Route path="/admin/question-banks/new" element={<AdminRoute><AdminQuestionBankEdit /></AdminRoute>} />
          <Route path="/admin/question-banks/:id" element={<AdminRoute><AdminQuestionBankEdit /></AdminRoute>} />
          <Route path="/results" element={<AdminRoute><ResultsList /></AdminRoute>} />
          <Route path="/results/:id" element={<AdminRoute><ResultDetail /></AdminRoute>} />
        </Routes>
      </main>
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};
